import { useMemo, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { queryParameters } from '~constants';
import { IdReference } from '~interfaces';

import Device from '../interfaces/device';
import SkcDevicesService from '../services/skcDevicesService';

const useUrlSearchParamsSkcDevice = (): [
	Device | IdReference | null,
	(value: Device | null) => void,
] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const service = useRef(new SkcDevicesService());

	const deviceId = searchParams.get(queryParameters.Device);

	const { data } = useSWR(
		deviceId != null ? [service.current.basePath, deviceId] : null,
		([_, args]) => service.current.getDeviceById(args),
		{
			// We will use this hook over multiple components. This prevent this from overfetching
			// but try to keep it up to date every once in a while. The organisation may change very
			// rarely
			dedupingInterval: 10000, // ms
		}
	);

	const urlDevice = useMemo<Device | IdReference | null>(() => {
		if (!deviceId) {
			return null;
		}

		return data ?? (deviceId != null ? { id: deviceId } : null);
	}, [deviceId, data]);

	const setDevice = (value: Device | null) => {
		setSearchParams((prev) => {
			if (value == null) {
				prev.delete(queryParameters.Device);
			} else {
				prev.set(queryParameters.Device, value.id);
			}

			return prev;
		});
	};

	return [urlDevice, setDevice];
};

export default useUrlSearchParamsSkcDevice;
